import React, { useEffect, useState } from 'react';
import { collection, orderBy, query, onSnapshot, doc, updateDoc, addDoc, deleteDoc, where, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';
import { signOut } from 'firebase/auth';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { migrateWorkers } from './migrateWorkers';
import updateWorkerSchedules from './updateWorkerSchedules';
import { logActivity, ACTIVITY_TYPES, TARGET_TYPES } from './utils/activityLogger';
import { 
  logDriverCreated, 
  logDriverUpdated, 
  logDriverDeleted, 
  logDriverScheduleUpdated,
  logBookingUpdated,
  logBookingStatusChanged
} from './utils/adminAuditLogger';

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);
  const [priceEdits, setPriceEdits] = useState({});
  
  // Service pricing management states
  const [showPriceManagement, setShowPriceManagement] = useState(false);
  const [servicePrices, setServicePrices] = useState({});
  const [priceFormEdits, setPriceFormEdits] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);
  const [priceSearchQuery, setPriceSearchQuery] = useState('');
  const [showAddServiceForm, setShowAddServiceForm] = useState(false);
  const [newServiceForm, setNewServiceForm] = useState({ name: '', category: 'Wash and Vac', price: '', startsAt: false });
  const [removingService, setRemovingService] = useState(null);

  // Worker management states
  const [workers, setWorkers] = useState([]);
  const [showWorkerManagement, setShowWorkerManagement] = useState(false);
  const [showWorkerForm, setShowWorkerForm] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);

  // Full-day assignment states
  const [showBlockPanel, setShowBlockPanel] = useState(null); // workerId or null
  const [blockDateInput, setBlockDateInput] = useState(() => new Date().toISOString().slice(0, 10));
  const [blockServiceInput, setBlockServiceInput] = useState('');
  const [blockingDay, setBlockingDay] = useState(false);
  const [workerForm, setWorkerForm] = useState({
    name: '',
    bio: '',
    imageUrl: '',
    start: '06:30',
    end: '14:00',
    interval: 90,
    dayOff: [1],
    overrides: null,
    lastSlotInclusive: false,
    customSlots: null,
    active: true,
    order: 0
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyDcVN1C_ZFgn1smrKc5TyWQPraFZk4rJas',
  });

  // Real-time listener for all bookings
  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate().toLocaleString() || 'N/A'
      }));
      setBookings(bookingsData);
      setFilteredBookings(bookingsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Real-time listener for workers
  useEffect(() => {
    console.log('🎧 Setting up workers real-time listener...');
    
    // Try without ordering first to avoid index issues
    const q = collection(db, 'workers');
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📡 Workers snapshot received at:', new Date().toLocaleTimeString());
      console.log('📊 Docs count:', snapshot.docs.length);
      
      const workersData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('👷 Worker loaded:', doc.id, data.name);
        return {
          id: doc.id,
          ...data
        };
      });
      
      // Sort manually by order field
      workersData.sort((a, b) => (a.order || 0) - (b.order || 0));
      console.log('✅ Workers state updated with', workersData.length, 'workers');
      setWorkers(workersData);
    }, (error) => {
      console.error('❌ Error loading workers:', error);
      console.log('This is normal if workers collection does not exist yet. Click "Migrate Workers" button to create it.');
      setWorkers([]);
    });

    return () => {
      console.log('🔌 Cleaning up workers listener');
      unsubscribe();
    };
  }, []);

  // Real-time listener for service prices
  useEffect(() => {
    const q = collection(db, 'service_prices');
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const prices = {};
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        prices[data.name] = {
          id: docSnap.id,
          ...data
        };
      });
      setServicePrices(prices);
      console.log('💰 Service prices loaded:', Object.keys(prices).length, 'services');
    }, (error) => {
      console.error('Error loading service prices:', error);
    });

    return () => unsubscribe();
  }, []);

  // Filter bookings
  useEffect(() => {
    let filtered = bookings;

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(b => b.status === filterStatus);
    }

    // Filter by date
    if (filterDate) {
      filtered = filtered.filter(b => b.date === filterDate);
    }

    setFilteredBookings(filtered);
  }, [filterStatus, filterDate, bookings]);

  // Update booking status
  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      const oldStatus = booking?.status || 'pending';
      
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Log the activity
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.UPDATE_BOOKING_STATUS,
        targetType: TARGET_TYPES.BOOKING,
        targetName: `Booking #${bookingId.slice(-6)} - ${booking?.name || 'Unknown'}`,
        changes: {
          before: { status: oldStatus },
          after: { status: newStatus }
        },
        description: `Changed booking status from ${oldStatus} to ${newStatus} for ${booking?.name || 'Unknown'}`
      });
      
      alert(`Booking status updated to ${newStatus}!`);
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status');
    }
  };

  // Update booking price
  const updateBookingPrice = async (bookingId, newPrice) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      const oldPrice = booking?.price || 0;
      
      await updateDoc(doc(db, 'bookings', bookingId), {
        price: Number(newPrice),
        updatedAt: new Date(),
      });
      
      // Log the activity
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.UPDATE_BOOKING_PRICE,
        targetType: TARGET_TYPES.PRICE,
        targetName: `Booking #${bookingId.slice(-6)} - ${booking?.name || 'Unknown'}`,
        changes: {
          before: { price: oldPrice },
          after: { price: Number(newPrice) }
        },
        description: `Updated price from $${oldPrice} to $${newPrice} for ${booking?.name || 'Unknown'}`
      });
      
      alert('Price updated!');
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Failed to update price');
    }
  };

  // Worker CRUD operations
  // Helper: Generate custom slots from start, end, interval
  const generateCustomSlots = (start, end, interval) => {
    const slots = [];
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let current = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const intMin = Number(interval);

    while (current + intMin <= endMin) {
      const h = Math.floor(current / 60);
      const m = current % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      current += intMin;
    }
    // Also push the last slot at the boundary if it fits exactly
    if (current === endMin) {
      const h = Math.floor(current / 60);
      const m = current % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }

    console.log(`📊 Generated ${slots.length} custom slots from ${start} to ${end} every ${interval} min:`, slots);
    return slots;
  };

  const handleAddWorker = async () => {
    try {
      // Validate times
      if (workerForm.start >= workerForm.end) {
        alert('⚠️ Error: Start time must be before end time!\n\nPlease use morning hours (AM) for start time and afternoon hours (PM) for end time.');
        return;
      }

      // Always regenerate custom slots from start/end/interval so booking form stays in sync
      const customSlots = generateCustomSlots(workerForm.start, workerForm.end, workerForm.interval);

      const workerData = {
        ...workerForm,
        customSlots: customSlots,
        interval: Number(workerForm.interval),
        dayOff: workerForm.dayOff,
        order: Number(workerForm.order)
      };
      
      const docRef = await addDoc(collection(db, 'workers'), workerData);
      
      // Log to activity logger (existing)
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.CREATE_WORKER,
        targetType: TARGET_TYPES.WORKER,
        targetName: workerForm.name,
        changes: {
          before: null,
          after: workerData
        },
        description: `Created new worker: ${workerForm.name} with schedule ${workerForm.start}-${workerForm.end}`
      });
      
      // 🔍 NEW: Log to admin audit system
      await logDriverCreated(docRef.id, {
        name: workerForm.name,
        schedule: `${workerForm.start}-${workerForm.end}`,
        dayOff: workerForm.dayOff,
        interval: workerForm.interval,
        active: workerForm.active,
        bio: workerForm.bio,
        email: workerForm.email || 'N/A',
        phone: workerForm.phone || 'N/A',
      });
      
      alert('Worker added successfully!');
      resetWorkerForm();
      setShowWorkerForm(false);
    } catch (error) {
      console.error('Error adding worker:', error);
      alert('Failed to add worker: ' + error.message);
    }
  };

  const handleUpdateWorker = async () => {
    try {
      console.log('🔧 Updating worker:', editingWorker.id, workerForm.name);
      console.log('📝 BEFORE (current in Firestore):', editingWorker);
      console.log('📝 FORM STATE (what admin entered):', workerForm);
      
      // Validate times
      if (workerForm.start >= workerForm.end) {
        alert('⚠️ Error: Start time must be before end time!\n\nPlease use morning hours (AM) for start time and afternoon hours (PM) for end time.\n\nExample:\nStart: 06:30 (6:30 AM)\nEnd: 16:00 (4:00 PM)');
        return;
      }
      
      // Always regenerate custom slots from start/end/interval so booking form stays in sync
      const customSlots = generateCustomSlots(workerForm.start, workerForm.end, workerForm.interval);
      
      const updatedData = {
        ...workerForm,
        customSlots: customSlots,
        interval: Number(workerForm.interval),
        dayOff: workerForm.dayOff,
        order: Number(workerForm.order)
      };
      
      console.log('📝 AFTER (will be saved):', updatedData);
      console.log('🔍 WHAT CHANGED:');
      console.log('  Name:', editingWorker.name, '→', updatedData.name);
      console.log('  Start Time:', editingWorker.start, '→', updatedData.start);
      console.log('  End Time:', editingWorker.end, '→', updatedData.end);
      console.log('  Day Off:', editingWorker.dayOff, '→', updatedData.dayOff);
      console.log('  Interval:', editingWorker.interval, '→', updatedData.interval);
      console.log('  Active:', editingWorker.active, '→', updatedData.active);
      console.log('  Order:', editingWorker.order, '→', updatedData.order);
      
      await updateDoc(doc(db, 'workers', editingWorker.id), updatedData);
      console.log('✅ Firestore update complete');
      
      // Log to activity logger (existing)
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.UPDATE_WORKER,
        targetType: TARGET_TYPES.WORKER,
        targetName: workerForm.name,
        changes: {
          before: editingWorker,
          after: updatedData
        },
        description: `Updated worker: ${workerForm.name}`
      });
      
      // 🔍 NEW: Log to admin audit system with detailed changes
      await logDriverUpdated(
        editingWorker.id,
        workerForm.name,
        {
          name: editingWorker.name,
          schedule: `${editingWorker.start}-${editingWorker.end}`,
          dayOff: editingWorker.dayOff,
          interval: editingWorker.interval,
          active: editingWorker.active,
          order: editingWorker.order
        },
        {
          name: updatedData.name,
          schedule: `${updatedData.start}-${updatedData.end}`,
          dayOff: updatedData.dayOff,
          interval: updatedData.interval,
          active: updatedData.active,
          order: updatedData.order
        }
      );
      
      // 🔍 If schedule changed specifically, log that too
      if (editingWorker.start !== updatedData.start || 
          editingWorker.end !== updatedData.end || 
          editingWorker.dayOff !== updatedData.dayOff) {
        await logDriverScheduleUpdated(
          editingWorker.id,
          workerForm.name,
          {
            start: editingWorker.start,
            end: editingWorker.end,
            dayOff: editingWorker.dayOff,
            interval: editingWorker.interval
          },
          {
            start: updatedData.start,
            end: updatedData.end,
            dayOff: updatedData.dayOff,
            interval: updatedData.interval
          },
          'Schedule updated via admin dashboard'
        );
      }
      
      alert('Worker updated successfully!');
      resetWorkerForm();
      setShowWorkerForm(false);
      setEditingWorker(null);
    } catch (error) {
      console.error('Error updating worker:', error);
      alert('Failed to update worker: ' + error.message);
    }
  };

  const handleDeleteWorker = async (workerId, workerName) => {
    if (window.confirm(`Are you sure you want to delete ${workerName}? This action cannot be undone.`)) {
      try {
        // Find the worker data before deleting
        const workerToDelete = workers.find(w => w.id === workerId);
        
        await deleteDoc(doc(db, 'workers', workerId));
        
        // Log to activity logger (existing)
        await logActivity({
          userEmail: auth.currentUser?.email || 'Unknown',
          action: ACTIVITY_TYPES.DELETE_WORKER,
          targetType: TARGET_TYPES.WORKER,
          targetName: workerName,
          changes: {
            before: workerToDelete,
            after: null
          },
          description: `Deleted worker: ${workerName}`
        });
        
        // 🔍 NEW: Log to admin audit system
        await logDriverDeleted(workerId, {
          name: workerName,
          schedule: workerToDelete ? `${workerToDelete.start}-${workerToDelete.end}` : 'Unknown',
          email: workerToDelete?.email || 'N/A',
          phone: workerToDelete?.phone || 'N/A',
          active: workerToDelete?.active
        });
        
        alert('Worker deleted successfully!');
      } catch (error) {
        console.error('Error deleting worker:', error);
        alert('Failed to delete worker: ' + error.message);
      }
    }
  };

  const handleEditWorker = (worker) => {
    console.log('✏️ EDIT MODAL OPENED for:', worker.name);
    console.log('📋 Loading worker data:', worker);
    setEditingWorker(worker);
    const formData = {
      name: worker.name,
      bio: worker.bio || '',
      imageUrl: worker.imageUrl || '',
      start: worker.start,
      end: worker.end,
      interval: worker.interval,
      dayOff: Array.isArray(worker.dayOff) ? worker.dayOff : [worker.dayOff],
      overrides: worker.overrides || null,
      lastSlotInclusive: worker.lastSlotInclusive || false,
      customSlots: worker.customSlots || null,
      active: worker.active !== undefined ? worker.active : true,
      order: worker.order || 0
    };
    console.log('📝 Form initialized with:', formData);
    setWorkerForm(formData);
    setShowWorkerForm(true);
  };

  const resetWorkerForm = () => {
    setWorkerForm({
      name: '',
      bio: '',
      imageUrl: '',
      start: '06:30',
      end: '14:00',
      interval: 90,
      dayOff: [1],
      overrides: null,
      lastSlotInclusive: false,
      customSlots: null,
      active: true,
      order: workers.length
    });
  };

  // Helper: extract date/service from a blockedDates entry (supports legacy plain strings)
  const getEntryDate = (entry) => (typeof entry === 'string' ? entry : entry.date);
  const getEntryService = (entry) => (typeof entry === 'string' ? '' : (entry.service || ''));

  const handleBlockDay = async (worker, dateStr) => {
    if (!dateStr) { alert('Please select a date.'); return; }
    const blockedDates = worker.blockedDates || [];
    if (blockedDates.some(e => getEntryDate(e) === dateStr)) {
      alert(`${worker.name} is already assigned a full day job on ${dateStr}.`);
      return;
    }
    const service = blockServiceInput.trim() || 'Full Day Job';
    const newEntry = { date: dateStr, service };
    const updated = [...blockedDates, newEntry].sort((a, b) => getEntryDate(a).localeCompare(getEntryDate(b)));
    try {
      setBlockingDay(true);
      await updateDoc(doc(db, 'workers', worker.id), { blockedDates: updated });
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.UPDATE_WORKER,
        targetType: TARGET_TYPES.WORKER,
        targetName: worker.name,
        changes: { before: { blockedDates }, after: { blockedDates: updated } },
        description: `Assigned ${worker.name} to full day job (${service}) on ${dateStr} — no customer slots available`
      });
      setBlockServiceInput('');
    } catch (error) {
      console.error('Error assigning full day job:', error);
      alert('Failed to assign full day job: ' + error.message);
    } finally {
      setBlockingDay(false);
    }
  };

  const handleUnblockDay = async (worker, dateStr) => {
    const blockedDates = (worker.blockedDates || []).filter(e => getEntryDate(e) !== dateStr);
    try {
      await updateDoc(doc(db, 'workers', worker.id), { blockedDates });
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.UPDATE_WORKER,
        targetType: TARGET_TYPES.WORKER,
        targetName: worker.name,
        changes: { before: { blockedDates: worker.blockedDates || [] }, after: { blockedDates } },
        description: `Cancelled full day assignment for ${worker.name} on ${dateStr} — slots restored`
      });
    } catch (error) {
      console.error('Error cancelling assignment:', error);
      alert('Failed to cancel assignment: ' + error.message);
    }
  };

  const handleMigrateWorkers = async () => {
    if (workers.length > 0) {
      if (!window.confirm('Workers already exist. This will add duplicate workers. Continue?')) {
        return;
      }
    }
    const result = await migrateWorkers();
    if (result.success) {
      // Log the migration
      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: ACTIVITY_TYPES.MIGRATE_WORKERS,
        targetType: TARGET_TYPES.WORKER,
        targetName: 'Initial Workers (Nick, Ricardo, Radcliffe)',
        changes: null,
        description: `Migrated initial workers from code to Firestore database`
      });
      alert('Workers migrated successfully!');
    } else {
      alert('Migration failed: ' + result.error);
    }
  };

  const handleUpdateSchedules = async () => {
    if (window.confirm('Update Nick and Ricardo to new custom time slots?')) {
      const result = await updateWorkerSchedules();
      if (result.success) {
        alert('✅ Schedules updated!\n\nNick: 6:30am, 8:00am, 9:30am, 11:00am, 12:30pm, 2:00pm\nRicardo: 6:30am, 8:30am, 10:30am, 12:30pm, 2:30pm, 4:30pm');
      } else {
        alert('Failed to update schedules: ' + result.error);
      }
    }
  };

  // ==========================================
  // SERVICE PRICING MANAGEMENT
  // ==========================================

  // Default services list (used for initial setup and as reference)
  const defaultServices = [
    // Wash and Vac
    { name: 'Wash and Vac (Small Car)', category: 'Wash and Vac', price: 2000, priceLabel: '$2,000', details: 'Vehicles like Vitz & Fit' },
    { name: 'Wash and Vac (Sedan)', category: 'Wash and Vac', price: 2200, priceLabel: '$2,200', details: 'Vehicles like Toyota Crown & Mark X' },
    { name: 'Wash and Vac (Small SUV)', category: 'Wash and Vac', price: 2800, priceLabel: '$2,800', details: 'Vehicles like HRV' },
    { name: 'Wash and Vac (Medium SUV)', category: 'Wash and Vac', price: 3000, priceLabel: '$3,000', details: 'Vehicles like CRV, RAV4' },
    { name: 'Wash and Vac (Large SUV)', category: 'Wash and Vac', price: 3500, priceLabel: '$3,500', details: 'Vehicles like Prado, Land Cruiser' },
    { name: 'Wash and Vac (Small Bus)', category: 'Wash and Vac', price: 3500, priceLabel: '$3,500', details: 'Vehicles like Voxy, Minivan, Pick up' },
    { name: 'Wash and Vac (Large Bus)', category: 'Wash and Vac', price: 4500, priceLabel: '$4,500', details: 'Vehicles like Large Hiace, Large Pick up' },
    { name: 'Wash and Vac (Pickup)', category: 'Wash and Vac', price: 4000, priceLabel: 'Starts at $4,000', details: 'Price varies by pickup size' },
    { name: 'Wash and Vac (Extra Large Bus)', category: 'Wash and Vac', price: 5000, priceLabel: '$5,000', details: 'Vehicles like Coaster' },
    { name: 'Wash and Vac (Small Truck)', category: 'Wash and Vac', price: 6000, priceLabel: '$6,000', details: 'Vehicles like Elf, Isuzu' },
    { name: 'Wash and Vac (Large Truck)', category: 'Wash and Vac', price: 7000, priceLabel: '$7,000', details: 'Vehicles like Isuzu' },
    { name: 'Wash and Vac (Trailer Head)', category: 'Wash and Vac', price: 6000, priceLabel: '$6,000', details: '' },
    { name: 'Wash and Vac (Trailer Front & Back)', category: 'Wash and Vac', price: 15000, priceLabel: '$15,000', details: '' },
    { name: 'Wash and Vac (Dumper Truck)', category: 'Wash and Vac', price: 13000, priceLabel: '$13,000', details: '' },
    { name: 'Wash and Vac (Tracker & Backhoe)', category: 'Wash and Vac', price: 15000, priceLabel: '$15,000', details: '' },
    // Detailing
    { name: 'Seat Only Detail', category: 'Detailing', price: 15000, priceLabel: 'Starts at $15,000', details: '' },
    { name: 'Full Interior Detail (with seat removal)', category: 'Detailing', price: 35000, priceLabel: 'Starts at $35,000', details: '' },
    { name: 'Headlight Restoration', category: 'Detailing', price: 3000, priceLabel: 'Starts at $3,000', details: '' },
    { name: 'Plastic Restoration', category: 'Detailing', price: 3000, priceLabel: 'Starts at $3,000', details: '' },
    { name: 'Buff and Polish', category: 'Detailing', price: 30000, priceLabel: 'Starts at $30,000', details: '' },
    { name: 'Undercarriage Wash', category: 'Detailing', price: 3000, priceLabel: 'Starts at $3,000', details: '' },
    { name: 'Engine Wash', category: 'Detailing', price: 3000, priceLabel: 'Starts at $3,000', details: '' },
    { name: 'Leather Interior Detail', category: 'Detailing', price: 10000, priceLabel: 'Starts at $10,000', details: '' },
    { name: 'Steam Cleaning Car Seat', category: 'Detailing', price: 15000, priceLabel: 'Starts at $15,000', details: '' },
    { name: 'Steam Clean Sofa', category: 'Detailing', price: 15000, priceLabel: 'Starts at $15,000', details: '' },
    // Specialty Cleaning
    { name: 'Wall Cleaning', category: 'Specialty Cleaning', price: 0, priceLabel: 'Please call for quote', details: '' },
    { name: 'Building Roof Cleaning', category: 'Specialty Cleaning', price: 15000, priceLabel: 'Starts at $15,000', details: '' },
    { name: 'Driveway Cleaning', category: 'Specialty Cleaning', price: 10000, priceLabel: 'Starts at $10,000', details: '' },
    { name: 'Carpet Cleaning', category: 'Specialty Cleaning', price: 5000, priceLabel: 'Starts at $5,000', details: '' },
    { name: 'Sofa Cleaning', category: 'Specialty Cleaning', price: 10000, priceLabel: 'Starts at $10,000', details: '' },
    { name: 'Mattress Cleaning', category: 'Specialty Cleaning', price: 10000, priceLabel: 'Starts at $10,000', details: '' },
  ];

  // Initialize prices in Firestore (one-time setup)
  const handleInitializePrices = async () => {
    if (Object.keys(servicePrices).length > 0) {
      if (!window.confirm('Service prices already exist in the database. This will reset ALL prices to defaults. Are you sure?')) {
        return;
      }
    }

    try {
      setSavingPrices(true);
      for (const service of defaultServices) {
        // Check if already exists
        if (servicePrices[service.name]) {
          await updateDoc(doc(db, 'service_prices', servicePrices[service.name].id), {
            price: service.price,
            priceLabel: service.priceLabel,
            updatedAt: new Date(),
            updatedBy: auth.currentUser?.email || 'Unknown'
          });
        } else {
          await addDoc(collection(db, 'service_prices'), {
            name: service.name,
            category: service.category,
            price: service.price,
            priceLabel: service.priceLabel,
            details: service.details,
            createdAt: new Date(),
            updatedAt: new Date(),
            updatedBy: auth.currentUser?.email || 'Unknown'
          });
        }
      }

      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: 'INITIALIZE_PRICES',
        targetType: TARGET_TYPES.PRICE,
        targetName: 'All Services',
        changes: null,
        description: `Initialized ${defaultServices.length} service prices in database`
      });

      alert(`✅ ${defaultServices.length} service prices initialized successfully!`);
    } catch (error) {
      console.error('Error initializing prices:', error);
      alert('Failed to initialize prices: ' + error.message);
    } finally {
      setSavingPrices(false);
    }
  };

  // Format price for display (e.g., 2000 → "$2,000")
  const formatPrice = (price) => {
    if (price === 0) return 'Call for quote';
    return `$${Number(price).toLocaleString()}`;
  };

  // Save a single service price
  const handleSaveServicePrice = async (serviceName) => {
    const editedPrice = priceFormEdits[serviceName];
    if (editedPrice === undefined) return;

    const numPrice = Number(editedPrice);
    if (isNaN(numPrice) || numPrice < 0) {
      alert('⚠️ Please enter a valid price (0 or greater)');
      return;
    }

    try {
      const existing = servicePrices[serviceName];
      if (!existing) {
        alert('Service not found in database. Please initialize prices first.');
        return;
      }

      const oldPrice = existing.price;
      const newPriceLabel = numPrice === 0 ? 'Please call for quote' : 
        (existing.priceLabel?.startsWith('Starts') ? `Starts at $${numPrice.toLocaleString()}` : `$${numPrice.toLocaleString()}`);

      await updateDoc(doc(db, 'service_prices', existing.id), {
        price: numPrice,
        priceLabel: newPriceLabel,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.email || 'Unknown'
      });

      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: 'UPDATE_SERVICE_PRICE',
        targetType: TARGET_TYPES.PRICE,
        targetName: serviceName,
        changes: {
          before: { price: oldPrice, priceLabel: existing.priceLabel },
          after: { price: numPrice, priceLabel: newPriceLabel }
        },
        description: `Updated ${serviceName} price from ${formatPrice(oldPrice)} to ${formatPrice(numPrice)}`
      });

      // Clear the edit for this service
      setPriceFormEdits(prev => {
        const updated = { ...prev };
        delete updated[serviceName];
        return updated;
      });

      alert(`✅ ${serviceName} price updated to ${formatPrice(numPrice)}`);
    } catch (error) {
      console.error('Error saving price:', error);
      alert('Failed to save price: ' + error.message);
    }
  };

  // Save all edited prices at once
  const handleSaveAllPrices = async () => {
    const editedServices = Object.keys(priceFormEdits);
    if (editedServices.length === 0) {
      alert('No price changes to save.');
      return;
    }

    // Validate all edits
    for (const name of editedServices) {
      const val = Number(priceFormEdits[name]);
      if (isNaN(val) || val < 0) {
        alert(`⚠️ Invalid price for "${name}". Please enter a valid number.`);
        return;
      }
    }

    if (!window.confirm(`Save price changes for ${editedServices.length} service(s)?`)) return;

    try {
      setSavingPrices(true);
      const changes = [];

      for (const serviceName of editedServices) {
        const numPrice = Number(priceFormEdits[serviceName]);
        const existing = servicePrices[serviceName];
        if (!existing) continue;

        const oldPrice = existing.price;
        const newPriceLabel = numPrice === 0 ? 'Please call for quote' :
          (existing.priceLabel?.startsWith('Starts') ? `Starts at $${numPrice.toLocaleString()}` : `$${numPrice.toLocaleString()}`);

        await updateDoc(doc(db, 'service_prices', existing.id), {
          price: numPrice,
          priceLabel: newPriceLabel,
          updatedAt: new Date(),
          updatedBy: auth.currentUser?.email || 'Unknown'
        });

        changes.push({ name: serviceName, from: formatPrice(oldPrice), to: formatPrice(numPrice) });
      }

      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: 'BULK_UPDATE_PRICES',
        targetType: TARGET_TYPES.PRICE,
        targetName: `${changes.length} services`,
        changes: { services: changes },
        description: `Bulk updated prices for ${changes.length} services`
      });

      setPriceFormEdits({});
      alert(`✅ ${changes.length} price(s) updated successfully!`);
    } catch (error) {
      console.error('Error bulk saving prices:', error);
      alert('Failed to save prices: ' + error.message);
    } finally {
      setSavingPrices(false);
    }
  };

  // Get services grouped by category for the price manager UI
  const getServicesByCategory = () => {
    const categories = {};
    const allServices = Object.keys(servicePrices).length > 0 ? 
      Object.values(servicePrices) : defaultServices;

    allServices.forEach(service => {
      const cat = service.category || 'Other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(service);
    });

    return categories;
  };

  // Add a new service to Firestore
  const handleAddService = async () => {
    const name = newServiceForm.name.trim();
    if (!name) { alert('⚠️ Please enter a service name.'); return; }
    if (servicePrices[name]) { alert(`⚠️ A service named "${name}" already exists.`); return; }
    const numPrice = Number(newServiceForm.price);
    if (isNaN(numPrice) || numPrice < 0) { alert('⚠️ Please enter a valid price.'); return; }

    const priceLabel = numPrice === 0 
      ? 'Please call for quote' 
      : (newServiceForm.startsAt ? `Starts at $${numPrice.toLocaleString()}` : `$${numPrice.toLocaleString()}`);

    try {
      setSavingPrices(true);
      await addDoc(collection(db, 'service_prices'), {
        name,
        category: newServiceForm.category,
        price: numPrice,
        priceLabel,
        details: '',
        createdAt: new Date(),
        createdBy: auth.currentUser?.email || 'Unknown'
      });

      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: 'ADD_SERVICE',
        targetType: TARGET_TYPES.PRICE,
        targetName: name,
        changes: { price: numPrice, priceLabel, category: newServiceForm.category },
        description: `Added new service: ${name} (${priceLabel})`
      });

      setNewServiceForm({ name: '', category: 'Wash and Vac', price: '', startsAt: false });
      setShowAddServiceForm(false);
      alert(`✅ "${name}" added successfully!`);
    } catch (error) {
      console.error('Error adding service:', error);
      alert('Failed to add service: ' + error.message);
    } finally {
      setSavingPrices(false);
    }
  };

  // Remove a service from Firestore
  const handleRemoveService = async (serviceName) => {
    const service = servicePrices[serviceName];
    if (!service) return;
    if (!window.confirm(`Are you sure you want to permanently remove "${serviceName}"? This will also remove it from the customer booking form.`)) return;

    try {
      setRemovingService(serviceName);
      await deleteDoc(doc(db, 'service_prices', service.id));

      await logActivity({
        userEmail: auth.currentUser?.email || 'Unknown',
        action: 'REMOVE_SERVICE',
        targetType: TARGET_TYPES.PRICE,
        targetName: serviceName,
        changes: { price: service.price, priceLabel: service.priceLabel, category: service.category },
        description: `Removed service: ${serviceName}`
      });

      alert(`✅ "${serviceName}" removed successfully.`);
    } catch (error) {
      console.error('Error removing service:', error);
      alert('Failed to remove service: ' + error.message);
    } finally {
      setRemovingService(null);
    }
  };

  // Stats calculation
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };

  // Service pricing map — uses Firestore prices if available, falls back to hardcoded defaults
  const hardcodedPricing = {
    'Wash and Vac (Small Car)': 2000,
    'Wash and Vac (Sedan)': 2200,
    'Wash and Vac (Small SUV)': 2800,
    'Wash and Vac (Medium SUV)': 3000,
    'Wash and Vac (Large SUV)': 3500,
    'Wash and Vac (Small Bus)': 3500,
    'Wash and Vac (Large Bus)': 4500,
    'Wash and Vac (Pickup)': 4000,
    'Wash and Vac (Extra Large Bus)': 5000,
    'Wash and Vac (Small Truck)': 6000,
    'Wash and Vac (Large Truck)': 7000,
    'Wash and Vac (Trailer Head)': 6000,
    'Wash and Vac (Trailer Front & Back)': 15000,
    'Wash and Vac (Dumper Truck)': 13000,
    'Wash and Vac (Tracker & Backhoe)': 15000,
    'Seat Only Detail': 15000,
    'Full Interior Detail (with seat removal)': 35000,
    'Headlight Restoration': 3000,
    'Plastic Restoration': 3000,
    'Buff and Polish': 30000,
    'Undercarriage Wash': 3000,
    'Engine Wash': 3000,
    'Leather Interior Detail': 10000,
    'Steam Cleaning Car Seat': 15000,
    'Steam Clean Sofa': 15000,
    'Wall Cleaning': 0,
    'Building Roof Cleaning': 15000,
    'Driveway Cleaning': 10000,
    'Carpet Cleaning': 5000,
    'Sofa Cleaning': 10000,
    'Mattress Cleaning': 10000,
  };

  // Build servicePricing from Firestore data, with hardcoded fallback
  const servicePricing = Object.keys(hardcodedPricing).reduce((acc, name) => {
    acc[name] = servicePrices[name]?.price ?? hardcodedPricing[name];
    return acc;
  }, {});

  // Monthly summary calculation
  const getMonthlySummary = () => {
    const [year, month] = selectedMonth.split('-');
    const monthlyBookings = bookings.filter(booking => {
      if (!booking.date) return false;
      const bookingDate = new Date(booking.date);
      return bookingDate.getFullYear() === parseInt(year) && 
             bookingDate.getMonth() + 1 === parseInt(month);
    });

    // Group by service
    const serviceStats = {};
    let totalRevenue = 0;

    monthlyBookings.forEach(booking => {
      const service = booking.service;
      if (!serviceStats[service]) {
        serviceStats[service] = {
          count: 0,
          revenue: 0,
          completed: 0,
          pending: 0,
          confirmed: 0,
          cancelled: 0,
        };
      }
      
      serviceStats[service].count++;
      serviceStats[service][booking.status || 'pending']++;
      
      // Calculate revenue (only for completed bookings)
      if (booking.status === 'completed') {
        const price = servicePricing[service] || 0;
        serviceStats[service].revenue += price;
        totalRevenue += price;
      }
    });

    return {
      bookings: monthlyBookings,
      serviceStats,
      totalRevenue,
      totalBookings: monthlyBookings.length,
      completedBookings: monthlyBookings.filter(b => b.status === 'completed').length,
    };
  };

  const monthlySummary = getMonthlySummary();

  const handleBack = async () => {
    await signOut(auth);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-sparkle-blue mb-1 sm:mb-2">Admin Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-600">Manage all bookings in real-time</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={handleBack}
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-red-500 text-white font-semibold rounded-full hover:bg-red-600 transition-colors text-sm sm:text-base"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 border-l-4 border-blue-500">
            <div className="text-xl sm:text-2xl font-bold text-gray-800">{stats.total}</div>
            <div className="text-xs sm:text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 border-l-4 border-yellow-500">
            <div className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-xs sm:text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 border-l-4 border-green-500">
            <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.confirmed}</div>
            <div className="text-xs sm:text-sm text-gray-600">Confirmed</div>
          </div>
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 border-l-4 border-blue-400">
            <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.completed}</div>
            <div className="text-xs sm:text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 border-l-4 border-red-500 col-span-2 sm:col-span-1">
            <div className="text-xl sm:text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <div className="text-xs sm:text-sm text-gray-600">Cancelled</div>
          </div>
        </div>

        {/* Monthly Summary Toggle */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => setShowMonthlySummary(!showMonthlySummary)}
            className="w-full bg-gradient-to-r from-sparkle-blue to-blue-600 text-white rounded-2xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-all"
          >
            <div className="flex justify-between items-center gap-2">
              <div className="text-left flex-1">
                <h2 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2 text-white">📊 Monthly Summary</h2>
                <p className="text-yellow-300 font-bold text-xs sm:text-lg">View monthly statistics</p>
              </div>
              <span className="text-2xl sm:text-3xl flex-shrink-0">{showMonthlySummary ? '▼' : '▶'}</span>
            </div>
          </button>
        </div>

        {/* Monthly Summary Content */}
        {showMonthlySummary && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Monthly Report</h2>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full sm:w-auto p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sparkle-blue text-sm sm:text-base"
              />
            </div>
            {/* Monthly Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 sm:p-6 border-2 border-blue-300">
                <div className="text-xs sm:text-sm text-blue-800 font-bold mb-1">Total Revenue (Completed)</div>
                <div className="text-2xl sm:text-3xl font-bold text-blue-900">
                  ${monthlySummary.totalRevenue.toLocaleString()}
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 sm:p-6 border-2 border-green-200">
                <div className="text-xs sm:text-sm text-green-700 font-semibold mb-1">Total Bookings</div>
                <div className="text-2xl sm:text-3xl font-bold text-green-800">
                  {monthlySummary.totalBookings}
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 sm:p-6 border-2 border-purple-200 sm:col-span-2 md:col-span-1">
                <div className="text-xs sm:text-sm text-purple-700 font-semibold mb-1">Completed Bookings</div>
                <div className="text-2xl sm:text-3xl font-bold text-purple-800">
                  {monthlySummary.completedBookings}
                </div>
              </div>
            </div>

            {/* Service Breakdown */}
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Service Breakdown</h3>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmed</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Cancelled</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-blue-600 uppercase font-bold">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(monthlySummary.serviceStats)
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .map(([service, stats]) => (
                      <tr key={service} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-900">{service}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">{stats.count}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-green-600 font-semibold">{stats.completed}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-yellow-600">{stats.pending}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-blue-600">{stats.confirmed}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-red-600">{stats.cancelled}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <span className="text-sm sm:text-lg font-bold text-blue-600 bg-blue-50 px-2 sm:px-3 py-1 rounded-lg">
                            ${stats.revenue.toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  {Object.keys(monthlySummary.serviceStats).length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-3 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
                        No bookings found for this month
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="text-xs sm:text-sm text-blue-800">
                <strong>Note:</strong> Revenue calculations are based on starting prices and only include completed bookings. 
                Actual revenue may vary based on vehicle size and additional services.
              </p>
            </div>
          </div>
        )}

        {/* Worker Management Toggle */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => setShowWorkerManagement(!showWorkerManagement)}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-2xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-all"
          >
            <div className="flex justify-between items-center gap-2">
              <div className="text-left flex-1">
                <h2 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">👷 Worker Management</h2>
                <p className="text-purple-200 font-bold text-xs sm:text-lg">Manage employees & schedules</p>
              </div>
              <span className="text-2xl sm:text-3xl flex-shrink-0">{showWorkerManagement ? '▼' : '▶'}</span>
            </div>
          </button>
        </div>

        {/* Worker Management Content */}
        {showWorkerManagement && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Manage Workers</h2>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {workers.length === 0 && (
                  <button
                    onClick={handleMigrateWorkers}
                    className="px-3 sm:px-4 py-2 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors text-sm sm:text-base whitespace-nowrap"
                  >
                    🔄 Migrate Workers
                  </button>
                )}
                <button
                  onClick={() => {
                    resetWorkerForm();
                    setEditingWorker(null);
                    setShowWorkerForm(!showWorkerForm);
                  }}
                  className="px-3 sm:px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
                >
                  {showWorkerForm ? '✕ Cancel' : '➕ Add Worker'}
                </button>
              </div>
            </div>

            {/* Worker Form */}
            {showWorkerForm && (
              <div className="bg-gray-50 rounded-xl p-6 mb-6 border-2 border-purple-200">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {editingWorker ? 'Edit Worker' : 'Add New Worker'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      value={workerForm.name}
                      onChange={(e) => setWorkerForm({ ...workerForm, name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                      placeholder="Worker name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Image URL</label>
                    <input
                      type="text"
                      value={workerForm.imageUrl}
                      onChange={(e) => setWorkerForm({ ...workerForm, imageUrl: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                      placeholder="https://example.com/image.png"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bio</label>
                    <textarea
                      value={workerForm.bio}
                      onChange={(e) => setWorkerForm({ ...workerForm, bio: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                      placeholder="Short bio about the worker"
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Start Time *</label>
                    <input
                      type="time"
                      value={workerForm.start}
                      onChange={(e) => {
                        console.log('⏰ START TIME CHANGED:', e.target.value);
                        setWorkerForm({ ...workerForm, start: e.target.value });
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                    />
                    {workerForm.start && (
                      <p className="text-xs text-blue-600 mt-1">
                        📅 {(() => {
                          const [h, m] = workerForm.start.split(':').map(Number);
                          const hour12 = h % 12 === 0 ? 12 : h % 12;
                          const ampm = h < 12 ? 'AM' : 'PM';
                          return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                        })()}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">End Time *</label>
                    <input
                      type="time"
                      value={workerForm.end}
                      onChange={(e) => {
                        console.log('⏰ END TIME CHANGED:', e.target.value);
                        setWorkerForm({ ...workerForm, end: e.target.value });
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                    />
                    {workerForm.end && (
                      <p className="text-xs text-blue-600 mt-1">
                        📅 {(() => {
                          const [h, m] = workerForm.end.split(':').map(Number);
                          const hour12 = h % 12 === 0 ? 12 : h % 12;
                          const ampm = h < 12 ? 'AM' : 'PM';
                          return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                        })()}
                      </p>
                    )}
                  </div>
                  {/* Time validation warning */}
                  {workerForm.start && workerForm.end && workerForm.start >= workerForm.end && (
                    <div className="col-span-2 p-3 bg-red-50 border border-red-300 rounded-lg">
                      <p className="text-red-700 text-sm font-semibold">
                        ⚠️ Warning: Start time must be before end time!
                      </p>
                      <p className="text-red-600 text-xs mt-1">
                        Current: {(() => {
                          const [h1, m1] = workerForm.start.split(':').map(Number);
                          const hour12_1 = h1 % 12 === 0 ? 12 : h1 % 12;
                          const ampm1 = h1 < 12 ? 'AM' : 'PM';
                          return `${hour12_1}:${m1.toString().padStart(2, '0')} ${ampm1}`;
                        })()} → {(() => {
                          const [h2, m2] = workerForm.end.split(':').map(Number);
                          const hour12_2 = h2 % 12 === 0 ? 12 : h2 % 12;
                          const ampm2 = h2 < 12 ? 'AM' : 'PM';
                          return `${hour12_2}:${m2.toString().padStart(2, '0')} ${ampm2}`;
                        })()}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Time Slot Interval (minutes) *</label>
                    <input
                      type="number"
                      value={workerForm.interval}
                      onChange={(e) => setWorkerForm({ ...workerForm, interval: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                      placeholder="90"
                      min="15"
                      step="15"
                    />
                    <p className="text-xs text-gray-500 mt-1">Booking slots will be created every X minutes</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Days Off *</label>
                    <div className="grid grid-cols-2 gap-2 p-3 border border-gray-300 rounded-lg">
                      {[{value: 0, label: 'Sunday'}, {value: 1, label: 'Monday'}, {value: 2, label: 'Tuesday'}, {value: 3, label: 'Wednesday'}, {value: 4, label: 'Thursday'}, {value: 5, label: 'Friday'}, {value: 6, label: 'Saturday'}].map(day => (
                        <label key={day.value} className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={(workerForm.dayOff || []).includes(day.value)}
                            onChange={(e) => {
                              const current = workerForm.dayOff || [];
                              const updated = e.target.checked
                                ? [...current, day.value].sort((a, b) => a - b)
                                : current.filter(d => d !== day.value);
                              setWorkerForm({ ...workerForm, dayOff: updated });
                            }}
                            className="w-4 h-4 text-purple-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{day.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Display Order</label>
                    <input
                      type="number"
                      value={workerForm.order}
                      onChange={(e) => setWorkerForm({ ...workerForm, order: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                      placeholder="0"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <select
                      value={workerForm.active}
                      onChange={(e) => setWorkerForm({ ...workerForm, active: e.target.value === 'true' })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Include Last Slot</label>
                    <select
                      value={workerForm.lastSlotInclusive}
                      onChange={(e) => setWorkerForm({ ...workerForm, lastSlotInclusive: e.target.value === 'true' })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Allow booking to start at end time</p>
                  </div>
                </div>

                {/* Live Booking Slots Preview */}
                {workerForm.start && workerForm.end && workerForm.interval && workerForm.start < workerForm.end && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <h4 className="text-sm font-bold text-blue-800 mb-2">📋 Booking Slots Preview (what customers will see):</h4>
                    <div className="flex flex-wrap gap-2">
                      {generateCustomSlots(workerForm.start, workerForm.end, workerForm.interval).map((slot, idx) => {
                        const [h, m] = slot.split(':').map(Number);
                        const hour12 = h % 12 === 0 ? 12 : h % 12;
                        const ampm = h < 12 ? 'AM' : 'PM';
                        return (
                          <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                            {hour12}:{m.toString().padStart(2, '0')} {ampm}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      {generateCustomSlots(workerForm.start, workerForm.end, workerForm.interval).length} slots total — These will update on the booking page immediately after saving.
                    </p>
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={editingWorker ? handleUpdateWorker : handleAddWorker}
                    className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {editingWorker ? '💾 Update Worker' : '➕ Add Worker'}
                  </button>
                  <button
                    onClick={() => {
                      setShowWorkerForm(false);
                      setEditingWorker(null);
                      resetWorkerForm();
                    }}
                    className="px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Workers List */}
            <div className="space-y-4">
              {workers.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg mb-4">No workers found</p>
                  <p className="text-gray-400 text-sm">Click "Migrate Workers" above to add the initial workers, or "Add Worker" to create a new one.</p>
                </div>
              ) : (
                workers.map((worker) => (
                  <div
                    key={worker.id}
                    className={`border-2 rounded-xl p-4 sm:p-6 transition-all ${
                      worker.active ? 'border-purple-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex gap-3 sm:gap-4">
                        {worker.imageUrl && (
                          <img
                            src={worker.imageUrl}
                            alt={worker.name}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-800">{worker.name}</h3>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              worker.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {worker.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {worker.bio && <p className="text-gray-600 text-xs sm:text-sm mb-3">{worker.bio}</p>}
                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500 font-semibold">Hours:</span>
                              <p className="text-gray-800">
                                {(() => {
                                  const [h1, m1] = worker.start.split(':').map(Number);
                                  const [h2, m2] = worker.end.split(':').map(Number);
                                  const start12 = `${h1 % 12 === 0 ? 12 : h1 % 12}:${m1.toString().padStart(2, '0')} ${h1 < 12 ? 'AM' : 'PM'}`;
                                  const end12 = `${h2 % 12 === 0 ? 12 : h2 % 12}:${m2.toString().padStart(2, '0')} ${h2 < 12 ? 'AM' : 'PM'}`;
                                  return `${start12} - ${end12}`;
                                })()}
                                <span className="text-xs text-gray-500 ml-1">({worker.start} - {worker.end})</span>
                              </p>
                              {worker.start >= worker.end && (
                                <p className="text-red-600 text-xs font-semibold mt-1">⚠️ Invalid schedule!</p>
                              )}
                            </div>
                            <div>
                              <span className="text-gray-500 font-semibold">Interval:</span>
                              <p className="text-gray-800">{worker.interval} min</p>
                            </div>
                            <div>
                              <span className="text-gray-500 font-semibold">Days Off:</span>
                              <p className="text-gray-800">
                                {(Array.isArray(worker.dayOff) ? worker.dayOff : [worker.dayOff])
                                  .map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
                                  .join(', ') || 'None'}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500 font-semibold">Order:</span>
                              <p className="text-gray-800">#{worker.order}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditWorker(worker)}
                          className="flex-1 px-3 sm:px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDeleteWorker(worker.id, worker.name)}
                          className="flex-1 px-3 sm:px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors text-sm"
                        >
                          🗑️ Delete
                        </button>
                      </div>

                      {/* Full Day Job Toggle */}
                      <button
                        onClick={() => {
                          setShowBlockPanel(showBlockPanel === worker.id ? null : worker.id);
                          setBlockDateInput(new Date().toISOString().slice(0, 10));
                          setBlockServiceInput('');
                        }}
                        className={`w-full mt-2 px-3 sm:px-4 py-2 font-semibold rounded-lg transition-colors text-sm ${
                          showBlockPanel === worker.id
                            ? 'bg-orange-600 text-white hover:bg-orange-700'
                            : 'bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200'
                        }`}
                      >
                        📋 {showBlockPanel === worker.id ? '✕ Close' : 'Full Day Job Assignments'}
                        {(worker.blockedDates || []).length > 0 && (
                          <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                            {(worker.blockedDates || []).length} assigned
                          </span>
                        )}
                      </button>

                      {/* Full Day Job Panel */}
                      {showBlockPanel === worker.id && (
                        <div className="mt-3 p-4 bg-orange-50 border-2 border-orange-200 rounded-xl">
                          <h4 className="font-bold text-orange-800 text-sm mb-3">
                            📋 Assign {worker.name} to a Full Day Job
                          </h4>

                          {/* Service / job input */}
                          <div className="mb-3">
                            <label className="block text-xs font-semibold text-orange-800 mb-1">
                              Job / Service occupying this day
                            </label>
                            <input
                              type="text"
                              list={`service-list-${worker.id}`}
                              value={blockServiceInput}
                              onChange={e => setBlockServiceInput(e.target.value)}
                              placeholder="e.g. Full Interior Detail, Buff and Polish…"
                              className="w-full px-3 py-2 rounded-lg text-sm border border-orange-300 focus:ring-2 focus:ring-orange-400 bg-white"
                            />
                            <datalist id={`service-list-${worker.id}`}>
                              {Object.keys(servicePrices).sort().map(name => (
                                <option key={name} value={name} />
                              ))}
                            </datalist>
                          </div>

                          {/* Quick select + date picker */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {(() => {
                              const today = new Date().toISOString().slice(0, 10);
                              const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
                              return (
                                <>
                                  <button
                                    onClick={() => setBlockDateInput(today)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                      blockDateInput === today
                                        ? 'bg-orange-500 text-white border-orange-500'
                                        : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-100'
                                    }`}
                                  >
                                    Today ({today})
                                  </button>
                                  <button
                                    onClick={() => setBlockDateInput(tomorrow)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                      blockDateInput === tomorrow
                                        ? 'bg-orange-500 text-white border-orange-500'
                                        : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-100'
                                    }`}
                                  >
                                    Tomorrow ({tomorrow})
                                  </button>
                                </>
                              );
                            })()}
                            <input
                              type="date"
                              value={blockDateInput}
                              onChange={e => setBlockDateInput(e.target.value)}
                              className="flex-1 min-w-0 px-3 py-1.5 rounded-lg text-xs border border-orange-300 focus:ring-2 focus:ring-orange-400 bg-white"
                            />
                          </div>

                          <button
                            onClick={() => handleBlockDay(worker, blockDateInput)}
                            disabled={blockingDay || !blockDateInput}
                            className="w-full py-2 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {blockingDay ? '⏳ Assigning...' : `📋 Assign ${worker.name} — ${blockDateInput}`}
                          </button>

                          {/* Assigned days list */}
                          {(worker.blockedDates || []).length > 0 && (
                            <div className="mt-4">
                              <p className="text-xs font-bold text-orange-800 mb-2">Full Day Assignments:</p>
                              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                                {[...(worker.blockedDates || [])]
                                  .sort((a, b) => getEntryDate(a).localeCompare(getEntryDate(b)))
                                  .map(entry => {
                                    const date = getEntryDate(entry);
                                    const svc = getEntryService(entry);
                                    return (
                                      <div key={date} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-orange-200 gap-2">
                                        <div className="min-w-0">
                                          <p className="text-sm text-gray-800 font-semibold">{date}</p>
                                          {svc && <p className="text-xs text-orange-600 truncate">{svc}</p>}
                                        </div>
                                        <button
                                          onClick={() => handleUnblockDay(worker, date)}
                                          className="text-xs text-red-500 font-bold hover:text-red-700 ml-1 whitespace-nowrap flex-shrink-0"
                                        >
                                          ✕ Cancel
                                        </button>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}

                          {(worker.blockedDates || []).length === 0 && (
                            <p className="mt-3 text-xs text-orange-600 text-center">No full day jobs assigned yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Service Pricing Toggle */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => setShowPriceManagement(!showPriceManagement)}
            className="w-full bg-gradient-to-r from-green-600 to-green-800 text-white rounded-2xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-all"
          >
            <div className="flex justify-between items-center gap-2">
              <div className="text-left flex-1">
                <h2 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">💰 Service Pricing</h2>
                <p className="text-green-200 font-bold text-xs sm:text-lg">Manage prices for all services</p>
              </div>
              <span className="text-2xl sm:text-3xl flex-shrink-0">{showPriceManagement ? '▼' : '▶'}</span>
            </div>
          </button>
        </div>

        {/* Service Pricing Content */}
        {showPriceManagement && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Manage Service Prices</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {Object.keys(servicePrices).length > 0 
                    ? `${Object.keys(servicePrices).length} services in database` 
                    : 'Prices not yet initialized — click "Initialize Prices" to start'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {Object.keys(servicePrices).length === 0 && (
                  <button
                    onClick={handleInitializePrices}
                    disabled={savingPrices}
                    className="px-3 sm:px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors text-sm sm:text-base whitespace-nowrap disabled:opacity-50"
                  >
                    {savingPrices ? '⏳ Saving...' : '🚀 Initialize Prices'}
                  </button>
                )}
                {Object.keys(servicePrices).length > 0 && (
                  <button
                    onClick={() => { setShowAddServiceForm(!showAddServiceForm); setNewServiceForm({ name: '', category: 'Wash and Vac', price: '', startsAt: false }); }}
                    className="px-3 sm:px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base whitespace-nowrap"
                  >
                    {showAddServiceForm ? '✖ Cancel' : '➕ Add Service'}
                  </button>
                )}
                {Object.keys(priceFormEdits).length > 0 && (
                  <>
                    <button
                      onClick={handleSaveAllPrices}
                      disabled={savingPrices}
                      className="px-3 sm:px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base whitespace-nowrap disabled:opacity-50"
                    >
                      {savingPrices ? '⏳ Saving...' : `💾 Save All (${Object.keys(priceFormEdits).length} changes)`}
                    </button>
                    <button
                      onClick={() => setPriceFormEdits({})}
                      className="px-3 sm:px-4 py-2 bg-gray-400 text-white font-semibold rounded-lg hover:bg-gray-500 transition-colors text-sm sm:text-base whitespace-nowrap"
                    >
                      ↩️ Discard All
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Add New Service Form */}
            {showAddServiceForm && (
              <div className="mb-5 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl">
                <h3 className="text-base font-bold text-purple-800 mb-3">➕ Add New Service</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Service Name *</label>
                    <input
                      type="text"
                      value={newServiceForm.name}
                      onChange={(e) => setNewServiceForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Ceramic Coating"
                      className="w-full p-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Category *</label>
                    <select
                      value={newServiceForm.category}
                      onChange={(e) => setNewServiceForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full p-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                    >
                      <option value="Wash and Vac">Wash and Vac</option>
                      <option value="Detailing">Detailing</option>
                      <option value="Specialty Cleaning">Specialty Cleaning</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Price (JMD) *</label>
                    <input
                      type="number"
                      value={newServiceForm.price}
                      onChange={(e) => setNewServiceForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="e.g. 5000"
                      className="w-full p-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                      min="0"
                      step="100"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input
                      type="checkbox"
                      id="startsAt"
                      checked={newServiceForm.startsAt}
                      onChange={(e) => setNewServiceForm(prev => ({ ...prev, startsAt: e.target.checked }))}
                      className="w-4 h-4 accent-purple-600"
                    />
                    <label htmlFor="startsAt" className="text-sm text-gray-700 font-medium">Show as "Starts at" price</label>
                  </div>
                </div>
                {newServiceForm.name && newServiceForm.price && (
                  <p className="text-xs text-purple-700 mb-3 font-medium">
                    Preview: <strong>{newServiceForm.name}</strong> — {newServiceForm.startsAt ? `Starts at $${Number(newServiceForm.price).toLocaleString()}` : `$${Number(newServiceForm.price).toLocaleString()}`}
                  </p>
                )}
                <button
                  onClick={handleAddService}
                  disabled={savingPrices || !newServiceForm.name.trim() || !newServiceForm.price}
                  className="px-4 py-2 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingPrices ? '⏳ Adding...' : '✅ Add Service'}
                </button>
              </div>
            )}

            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                value={priceSearchQuery}
                onChange={(e) => setPriceSearchQuery(e.target.value)}
                placeholder="🔍 Search services..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 text-sm sm:text-base"
              />
            </div>

            {/* Price Cards by Category */}
            {Object.entries(getServicesByCategory()).map(([category, services]) => {
              // Filter by search query
              const filtered = services.filter(s => 
                s.name.toLowerCase().includes(priceSearchQuery.toLowerCase())
              );
              if (filtered.length === 0) return null;

              const categoryIcons = {
                'Wash and Vac': '🚿',
                'Detailing': '✨',
                'Specialty Cleaning': '🏠'
              };

              return (
                <div key={category} className="mb-6">
                  <h3 className="text-lg font-bold text-gray-700 mb-3 border-b-2 border-gray-200 pb-2">
                    {categoryIcons[category] || '📦'} {category} ({filtered.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((service) => {
                      const currentPrice = service.price ?? 0;
                      const editedPrice = priceFormEdits[service.name];
                      const hasEdit = editedPrice !== undefined && Number(editedPrice) !== currentPrice;
                      const displayPrice = editedPrice !== undefined ? editedPrice : currentPrice;

                      return (
                        <div 
                          key={service.name} 
                          className={`p-4 rounded-xl border-2 transition-all ${
                            hasEdit 
                              ? 'border-yellow-400 bg-yellow-50 shadow-md' 
                              : 'border-gray-200 bg-gray-50 hover:border-green-300'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-gray-800 text-sm leading-tight flex-1 mr-2">
                              {service.name}
                            </h4>
                            <div className="flex items-center gap-1">
                              {hasEdit && (
                                <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                                  Modified
                                </span>
                              )}
                              <button
                                onClick={() => handleRemoveService(service.name)}
                                disabled={removingService === service.name}
                                title="Remove this service"
                                className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded hover:bg-red-500 hover:text-white transition-colors font-bold disabled:opacity-50"
                              >
                                {removingService === service.name ? '⏳' : '🗑'}
                              </button>
                            </div>
                          </div>

                          {service.details && (
                            <p className="text-xs text-gray-500 mb-2">{service.details}</p>
                          )}

                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-gray-500 text-sm font-medium">$</span>
                            <input
                              type="number"
                              value={displayPrice}
                              onChange={(e) => setPriceFormEdits(prev => ({
                                ...prev,
                                [service.name]: e.target.value
                              }))}
                              className={`flex-1 p-2 border rounded-lg text-sm font-bold ${
                                hasEdit ? 'border-yellow-400 bg-white' : 'border-gray-300'
                              }`}
                              min="0"
                              step="100"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                              {service.priceLabel || formatPrice(currentPrice)}
                            </p>
                            {hasEdit && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleSaveServicePrice(service.name)}
                                  className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 font-semibold"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setPriceFormEdits(prev => {
                                    const updated = { ...prev };
                                    delete updated[service.name];
                                    return updated;
                                  })}
                                  className="text-xs px-2 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 font-semibold"
                                >
                                  Undo
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Show price change preview */}
                          {hasEdit && (
                            <div className="mt-2 p-2 bg-yellow-100 rounded text-xs">
                              <span className="text-gray-500 line-through">{formatPrice(currentPrice)}</span>
                              <span className="mx-1">→</span>
                              <span className="text-green-700 font-bold">{formatPrice(Number(editedPrice))}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* No results message */}
            {priceSearchQuery && Object.values(getServicesByCategory()).every(services => 
              services.filter(s => s.name.toLowerCase().includes(priceSearchQuery.toLowerCase())).length === 0
            ) && (
              <div className="text-center py-8 text-gray-500">
                <p className="text-4xl mb-2">🔍</p>
                <p>No services found matching "{priceSearchQuery}"</p>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sparkle-blue text-sm sm:text-base"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sparkle-blue text-sm sm:text-base"
              />
            </div>
          </div>
        </div>

        {/* Bookings Table/Cards */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">
              Bookings ({filteredBookings.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading bookings...</div>
          ) : filteredBookings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No bookings found</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredBookings.map((booking) => {
                      const priceValue = priceEdits[booking.id] !== undefined ? priceEdits[booking.id] : (booking.price !== undefined ? booking.price : servicePricing[booking.service] || 0);
                      const statusColors = {
                        pending: 'bg-yellow-100 text-yellow-800',
                        confirmed: 'bg-green-100 text-green-800',
                        completed: 'bg-blue-100 text-blue-800',
                        cancelled: 'bg-red-100 text-red-800'
                      };
                      
                      return (
                        <tr key={booking.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{booking.name}</div>
                            <div className="text-sm text-gray-500">{booking.email}</div>
                            <div className="text-sm text-gray-500">{booking.phone}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{booking.service}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{booking.vehicleSize || 'N/A'}</td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-gray-900">{booking.date}</div>
                            <div className="text-sm text-gray-500">{booking.time}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">{booking.location}</div>
                            {booking.lat && booking.lng && (
                              <button
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowMapModal(true);
                                }}
                                className="text-xs text-sparkle-blue hover:underline mt-1 flex items-center gap-1"
                              >
                                📍 View on Map
                              </button>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{booking.worker || 'Not Assigned'}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              booking.paymentMethod === 'Cash' 
                                ? 'bg-green-100 text-green-800' 
                                : booking.paymentMethod === 'Bank Transfer'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.paymentMethod || 'Not Selected'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                className="w-24 p-1 border border-gray-300 rounded text-sm"
                                value={priceValue}
                                onChange={e => setPriceEdits({ ...priceEdits, [booking.id]: e.target.value })}
                              />
                              <button
                                className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition-colors"
                                onClick={() => updateBookingPrice(booking.id, priceEdits[booking.id] !== undefined ? priceEdits[booking.id] : priceValue)}
                                disabled={Number(priceValue) === Number(booking.price)}
                              >
                                Save
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors[booking.status || 'pending']}`}>
                              {(booking.status || 'pending').charAt(0).toUpperCase() + (booking.status || 'pending').slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              {booking.status !== 'confirmed' && booking.status !== 'completed' && booking.status !== 'cancelled' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  className="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition-colors"
                                >
                                  ✓ Confirm
                                </button>
                              )}
                              {booking.status === 'confirmed' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'completed')}
                                  className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                                >
                                  ✓ Complete
                                </button>
                              )}
                              {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                  className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
                                >
                                  ✗ Cancel
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-gray-200">
                {filteredBookings.map((booking) => {
                  const priceValue = priceEdits[booking.id] !== undefined ? priceEdits[booking.id] : (booking.price !== undefined ? booking.price : servicePricing[booking.service] || 0);
                  const statusColors = {
                    pending: 'bg-yellow-100 text-yellow-800',
                    confirmed: 'bg-green-100 text-green-800',
                    completed: 'bg-blue-100 text-blue-800',
                    cancelled: 'bg-red-100 text-red-800'
                  };
                  
                  return (
                    <div key={booking.id} className="p-4 space-y-3">
                      {/* Header */}
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-gray-900">{booking.name}</h3>
                          <p className="text-xs text-gray-500">{booking.email}</p>
                          <p className="text-xs text-gray-500">{booking.phone}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[booking.status || 'pending']}`}>
                          {(booking.status || 'pending').charAt(0).toUpperCase() + (booking.status || 'pending').slice(1)}
                        </span>
                      </div>

                      {/* Service Info */}
                      <div className="text-sm space-y-1">
                        <p><strong>Service:</strong> {booking.service}</p>
                        <p><strong>Vehicle:</strong> {booking.vehicleSize || 'N/A'}</p>
                        <p><strong>Date:</strong> {booking.date} at {booking.time}</p>
                        <p><strong>Worker:</strong> {booking.worker || 'Not Assigned'}</p>
                        <div className="flex items-center justify-between">
                          <strong>Location:</strong>
                          {booking.lat && booking.lng && (
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowMapModal(true);
                              }}
                              className="text-xs text-sparkle-blue hover:underline"
                            >
                              📍 View Map
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 truncate">{booking.location}</p>
                      </div>

                      {/* Payment & Price */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          booking.paymentMethod === 'Cash' 
                            ? 'bg-green-100 text-green-800' 
                            : booking.paymentMethod === 'Bank Transfer'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {booking.paymentMethod || 'No Payment'}
                        </span>
                        <div className="flex items-center gap-1 flex-1">
                          <input
                            type="number"
                            min="0"
                            className="flex-1 min-w-0 p-1 border border-gray-300 rounded text-sm"
                            value={priceValue}
                            onChange={e => setPriceEdits({ ...priceEdits, [booking.id]: e.target.value })}
                          />
                          <button
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onClick={() => updateBookingPrice(booking.id, priceEdits[booking.id] !== undefined ? priceEdits[booking.id] : priceValue)}
                            disabled={Number(priceValue) === Number(booking.price)}
                          >
                            $
                          </button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        {booking.status !== 'confirmed' && booking.status !== 'completed' && booking.status !== 'cancelled' && (
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                            className="flex-1 text-xs bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 transition-colors"
                          >
                            ✓ Confirm
                          </button>
                        )}
                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'completed')}
                            className="flex-1 text-xs bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 transition-colors"
                          >
                            ✓ Complete
                          </button>
                        )}
                        {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                            className="flex-1 text-xs bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 transition-colors"
                          >
                            ✗ Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Map Modal */}
        {showMapModal && selectedBooking && isLoaded && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-sparkle-blue">Booking Location</h3>
                  <p className="text-gray-600">{selectedBooking.name} - {selectedBooking.service}</p>
                </div>
                <button
                  onClick={() => setShowMapModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4 space-y-2">
                  <p className="text-sm text-gray-700"><strong>📅 Date & Time:</strong> {selectedBooking.date} at {selectedBooking.time}</p>
                  <p className="text-sm text-gray-700"><strong>📍 Address:</strong> {selectedBooking.location}</p>
                  <p className="text-sm text-gray-700"><strong>🗺️ Coordinates:</strong> {selectedBooking.lat}, {selectedBooking.lng}</p>
                  <p className="text-sm text-gray-700"><strong>🚗 Vehicle:</strong> {selectedBooking.vehicleSize || 'N/A'}</p>
                  <p className="text-sm text-gray-700"><strong>👤 Worker:</strong> {selectedBooking.worker || 'Not Assigned'}</p>
                  <p className="text-sm text-gray-700"><strong>💳 Payment Method:</strong> {selectedBooking.paymentMethod || 'Not Selected'}</p>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${selectedBooking.lat},${selectedBooking.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-sparkle-blue text-white font-semibold rounded-full hover:bg-blue-600 transition-colors mb-4"
                >
                  🗺️ Open in Google Maps
                </a>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '400px', borderRadius: '1rem' }}
                  center={{ lat: selectedBooking.lat, lng: selectedBooking.lng }}
                  zoom={15}
                >
                  <Marker position={{ lat: selectedBooking.lat, lng: selectedBooking.lng }} />
                </GoogleMap>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}